// v2 flamingo — the field marks: the one-leg stance (one stilt leg with a knee
// knuckle, the other folded up under the belly as a thigh stub, a horizontal
// tucked shank and a dangling foot), a rounded pink body with deeper rose wing
// coverts and the black flight-feather tips peeking under a perky pink tail, a
// three-bend question-mark neck and a pale bill that bends sharply down into a
// black tip. Two plumages: v0 rosy greater flamingo, v1 vivid coral Caribbean
// flamingo. Ported 1:1 from age-of-mcp's v2/flamingo.ts, using the library's
// shared `wetland` (eyes, leg) helpers.
import * as THREE from "three";
import { defineModel, wetland, type Part } from "../../lib";

const PLUMAGES = [
  { body: "#f2a4b8", covert: "#e87a96", neck: "#f4acbe", head: "#f6b4c4",
    legc: "#e8889a", billBase: "#f0ccd4" },
  { body: "#f08498", covert: "#e25a7c", neck: "#f28ca0", head: "#f494a8",
    legc: "#e06a80", billBase: "#f2c4cc" },
];

function flamingoParts(v: number): Part[] {
  const c = PLUMAGES[v % PLUMAGES.length]!;
  const parts: Part[] = [
    // the standing stilt leg with its knee knuckle
    ...wetland.leg(0.002, 0.006, 0.21, 0.0032, c.legc),
    { geometry: new THREE.IcosahedronGeometry(0.0042, 0), color: c.legc,
      position: [0.002, 0.1, 0.006] },
    // the folded leg tucked under the belly: thigh stub, horizontal
    // shank, dangling foot
    { geometry: new THREE.CylinderGeometry(0.0035, 0.0032, 0.035, 4), color: c.legc,
      position: [-0.004, 0.172, -0.016], rotation: [0, 0, 0.3] },
    { geometry: new THREE.CylinderGeometry(0.003, 0.003, 0.05, 4), color: c.legc,
      position: [-0.022, 0.157, -0.018], rotation: [0, 0, Math.PI / 2 + 0.2] },
    { geometry: new THREE.BoxGeometry(0.012, 0.003, 0.006), color: c.legc,
      position: [-0.048, 0.151, -0.018] },
    // rounded body with deeper rose wing coverts
    { geometry: new THREE.IcosahedronGeometry(0.042, 0), color: c.body,
      position: [0, 0.21, 0], scale: [1.45, 0.95, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.03, 0), color: c.covert,
      position: [-0.008, 0.218, 0.02], scale: [1.3, 0.8, 0.5] },
    { geometry: new THREE.IcosahedronGeometry(0.03, 0), color: c.covert,
      position: [-0.008, 0.218, -0.02], scale: [1.3, 0.8, 0.5] },
    // black flight tips peeking under a perky pink tail
    { geometry: new THREE.ConeGeometry(0.01, 0.035, 4), color: "#2a2426",
      position: [-0.052, 0.21, 0], rotation: [0, 0, Math.PI / 2 + 0.35],
      scale: [1, 1, 0.5] },
    { geometry: new THREE.ConeGeometry(0.011, 0.03, 4), color: c.body,
      position: [-0.05, 0.228, 0], rotation: [0, 0, Math.PI / 2 - 0.4],
      scale: [1, 1, 0.6] },
    // three-bend question-mark neck rising off the chest
    { geometry: new THREE.CylinderGeometry(0.0065, 0.009, 0.065, 5), color: c.neck,
      position: [0.04, 0.252, 0], rotation: [0, 0, -0.35] },
    { geometry: new THREE.CylinderGeometry(0.0055, 0.0065, 0.06, 5), color: c.neck,
      position: [0.062, 0.305, 0] },
    { geometry: new THREE.CylinderGeometry(0.005, 0.0055, 0.045, 5), color: c.neck,
      position: [0.068, 0.348, 0], rotation: [0, 0, 0.55] },
    // head tipping forward-down
    { geometry: new THREE.IcosahedronGeometry(0.012, 0), color: c.head,
      position: [0.082, 0.362, 0], scale: [1.2, 0.95, 0.9] },
    // the bent bill — pale base kinking into a black tip
    { geometry: new THREE.ConeGeometry(0.006, 0.02, 4), color: c.billBase,
      position: [0.095, 0.352, 0], rotation: [0, 0, -2.0] },
    { geometry: new THREE.ConeGeometry(0.0045, 0.016, 4), color: "#1a1a1a",
      position: [0.101, 0.338, 0], rotation: [0, 0, -2.7] },
    ...wetland.eyes(0.084, 0.366, 0.009, 0.0028),
  ];
  return parts;
}

export const flamingoV2 = defineModel({
  id: "flamingo-v2",
  batchId: "fable",
  name: "Flamingo (v2)",
  tags: ["creature","flamingo","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly flamingo.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.08,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => flamingoParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => flamingoParts(1) },
  ],
  animations: [],
});
